import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../../components/ui/table';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../components/ui/tabs';
import { toast } from 'sonner';
import { 
  Calendar, Clock, Users, Sun, Cloud, Check, X, 
  ChevronLeft, ChevronRight, Building2, RefreshCw
} from 'lucide-react';
import { useAuth } from '../../context/AuthContext';
import axios from 'axios';
import { formatDateToLocal, getTodayLocal } from '../../utils/dateUtils';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const getAuthHeaders = () => {
  const token = localStorage.getItem('token') || localStorage.getItem('fortis_token');
  return token ? { Authorization: `Bearer ${token}` } : {};
};

const ManagerDailyAttendance = () => {
  const { user } = useAuth();
  const [loading, setLoading] = useState(false);
  const [selectedDate, setSelectedDate] = useState(getTodayLocal());
  const [attendanceData, setAttendanceData] = useState(null);
  const [historyData, setHistoryData] = useState(null);
  const [activeTab, setActiveTab] = useState('today');

  useEffect(() => {
    if (activeTab === 'today') {
      loadTodayAttendance();
    } else {
      loadHistory();
    }
  }, [selectedDate, activeTab]);

  const loadTodayAttendance = async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/manager/daily-attendance`, {
        params: { date: selectedDate },
        headers: getAuthHeaders()
      });
      setAttendanceData(res.data);
    } catch (error) {
      console.error('Mesai verileri yüklenirken hata:', error);
      if (error.response?.status === 403) {
        toast.error('Bu sayfaya erişim yetkiniz yok');
      } else {
        toast.error('Veriler yüklenemedi');
      }
    }
    setLoading(false);
  };

  const loadHistory = async () => {
    setLoading(true);
    try {
      const endDate = selectedDate;
      const startDate = new Date(new Date(selectedDate).getTime() - 7 * 24 * 60 * 60 * 1000)
        .toISOString().split('T')[0];
    // Yerel tarih formatla
    const formattedDate = formatDateToLocal(endDate);
      
      const res = await axios.get(`${API}/manager/daily-attendance/history`, {
        params: { start_date: startDate, end_date: endDate },
        headers: getAuthHeaders()
      });
      setHistoryData(res.data);
    } catch (error) {
      console.error('Geçmiş verileri yüklenirken hata:', error);
      toast.error('Geçmiş yüklenemedi');
    }
    setLoading(false);
  };

  const changeDate = (days) => {
    const newDate = new Date(selectedDate);
    newDate.setDate(newDate.getDate() + days);
    setSelectedDate(formatDateToLocal(newDate));
  };

  const goToToday = () => {
    setSelectedDate(getTodayLocal());
  };

  const formatDate = (dateStr) => {
    const date = new Date(dateStr);
    return date.toLocaleDateString('tr-TR', { 
      weekday: 'long', 
      year: 'numeric', 
      month: 'long', 
      day: 'numeric' 
    });
  };

  const isToday = selectedDate === getTodayLocal();

  return (
    <div className="space-y-6 p-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-800" data-testid="page-title">
            Günlük Mesai Bildirimleri
          </h1>
          <p className="text-gray-500">Çalışanların iş başlangıcı bildirimlerini görüntüleyin</p>
        </div>
        <Button onClick={loadTodayAttendance} variant="outline" disabled={loading}>
          <RefreshCw className={`h-4 w-4 mr-2 ${loading ? 'animate-spin' : ''}`} />
          Yenile
        </Button>
      </div>

      {/* Tabs */}
      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList className="grid w-full max-w-md grid-cols-2">
          <TabsTrigger value="today" data-testid="tab-today">
            <Calendar className="h-4 w-4 mr-2" />
            Günlük Görünüm
          </TabsTrigger>
          <TabsTrigger value="history" data-testid="tab-history">
            <Clock className="h-4 w-4 mr-2" />
            Geçmiş
          </TabsTrigger>
        </TabsList>

        {/* Today Tab */}
        <TabsContent value="today" className="space-y-4">
          {/* Date Selector */}
          <Card>
            <CardContent className="py-4">
              <div className="flex items-center justify-between">
                <Button variant="ghost" size="sm" onClick={() => changeDate(-1)}>
                  <ChevronLeft className="h-5 w-5" />
                </Button>
                <div className="flex items-center gap-4">
                  <Input
                    type="date"
                    value={selectedDate}
                    onChange={(e) => setSelectedDate(e.target.value)}
                    className="w-auto"
                  />
                  <span className="text-lg font-medium text-gray-700">
                    {formatDate(selectedDate)}
                  </span>
                  {!isToday && (
                    <Button variant="outline" size="sm" onClick={goToToday}>
                      Bugün
                    </Button>
                  )}
                </div>
                <Button variant="ghost" size="sm" onClick={() => changeDate(1)}>
                  <ChevronRight className="h-5 w-5" />
                </Button>
              </div>
            </CardContent>
          </Card>

          {/* Summary Cards */}
          {attendanceData && (
            <div className="grid grid-cols-3 gap-4">
              <Card className="bg-blue-50 border-blue-200">
                <CardContent className="py-4">
                  <div className="flex items-center gap-3">
                    <Users className="h-8 w-8 text-blue-600" />
                    <div>
                      <p className="text-sm text-blue-600">Toplam Bildirim</p>
                      <p className="text-2xl font-bold text-blue-700">{attendanceData.summary.total}</p>
                    </div>
                  </div>
                </CardContent>
              </Card>
              <Card className="bg-orange-50 border-orange-200">
                <CardContent className="py-4">
                  <div className="flex items-center gap-3">
                    <Sun className="h-8 w-8 text-orange-600" />
                    <div>
                      <p className="text-sm text-orange-600">Sabah Bildirimi</p>
                      <p className="text-2xl font-bold text-orange-700">{attendanceData.summary.sabah_count}</p>
                    </div>
                  </div>
                </CardContent>
              </Card>
              <Card className="bg-cyan-50 border-cyan-200">
                <CardContent className="py-4">
                  <div className="flex items-center gap-3">
                    <Cloud className="h-8 w-8 text-cyan-600" />
                    <div>
                      <p className="text-sm text-cyan-600">Öğlen Bildirimi</p>
                      <p className="text-2xl font-bold text-cyan-700">{attendanceData.summary.oglen_count}</p>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </div>
          )}

          {/* Attendance Table */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Users className="h-5 w-5" />
                Çalışan Bildirimleri
              </CardTitle>
            </CardHeader>
            <CardContent>
              {loading ? (
                <div className="text-center py-8 text-gray-500">Yükleniyor...</div>
              ) : attendanceData?.records?.length > 0 ? (
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Çalışan</TableHead>
                      <TableHead>Şube</TableHead>
                      <TableHead className="text-center">
                        <div className="flex items-center justify-center gap-1">
                          <Sun className="h-4 w-4 text-orange-500" />
                          Sabah
                        </div>
                      </TableHead>
                      <TableHead className="text-center">
                        <div className="flex items-center justify-center gap-1">
                          <Cloud className="h-4 w-4 text-cyan-500" />
                          Öğlen
                        </div>
                      </TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {attendanceData.records.map((record) => (
                      <TableRow key={record.id} data-testid={`attendance-row-${record.id}`}>
                        <TableCell className="font-medium">{record.employee_name}</TableCell>
                        <TableCell>
                          <div className="flex items-center gap-1 text-sm text-gray-600">
                            <Building2 className="h-3 w-3" />
                            {record.branch_name || '-'}
                          </div>
                        </TableCell>
                        <TableCell className="text-center">
                          {record.sabah ? (
                            <div className="flex flex-col items-center">
                              <Badge className="bg-green-100 text-green-700 hover:bg-green-100">
                                <Check className="h-3 w-3 mr-1" />
                                Geldi
                              </Badge>
                              <span className="text-xs text-gray-500 mt-1">{record.sabah_time}</span>
                            </div>
                          ) : (
                            <Badge variant="outline" className="text-gray-400">
                              <X className="h-3 w-3 mr-1" />
                              -
                            </Badge>
                          )}
                        </TableCell>
                        <TableCell className="text-center">
                          {record.oglen ? (
                            <div className="flex flex-col items-center">
                              <Badge className="bg-green-100 text-green-700 hover:bg-green-100">
                                <Check className="h-3 w-3 mr-1" />
                                Geldi
                              </Badge>
                              <span className="text-xs text-gray-500 mt-1">{record.oglen_time}</span>
                            </div>
                          ) : (
                            <Badge variant="outline" className="text-gray-400">
                              <X className="h-3 w-3 mr-1" />
                              -
                            </Badge>
                          )}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              ) : (
                <div className="text-center py-8 text-gray-500">
                  <Users className="h-12 w-12 mx-auto mb-3 text-gray-300" />
                  <p>Bu tarihte bildirim bulunmuyor</p>
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* History Tab */}
        <TabsContent value="history" className="space-y-4">
          <Card>
            <CardHeader>
              <CardTitle>Son 7 Günlük Geçmiş</CardTitle>
            </CardHeader>
            <CardContent>
              {loading ? (
                <div className="text-center py-8 text-gray-500">Yükleniyor...</div>
              ) : historyData?.history ? (
                <div className="space-y-4">
                  {Object.entries(historyData.history)
                    .sort(([a], [b]) => b.localeCompare(a))
                    .map(([date, records]) => (
                      <div key={date} className="border rounded-lg p-4">
                        <h3 className="font-semibold text-gray-700 mb-3 flex items-center gap-2">
                          <Calendar className="h-4 w-4" />
                          {formatDate(date)}
                          <Badge variant="outline" className="ml-2">
                            {records.length} kişi
                          </Badge>
                        </h3>
                        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-2">
                          {records.map((r, idx) => (
                            <div key={idx} className="bg-gray-50 rounded p-2 text-sm">
                              <p className="font-medium truncate">{r.employee_name}</p>
                              <div className="flex gap-2 mt-1 text-xs text-gray-500">
                                {r.sabah && (
                                  <span className="flex items-center gap-1">
                                    <Sun className="h-3 w-3 text-orange-400" />
                                    {r.sabah_time}
                                  </span>
                                )}
                                {r.oglen && (
                                  <span className="flex items-center gap-1">
                                    <Cloud className="h-3 w-3 text-cyan-400" />
                                    {r.oglen_time}
                                  </span>
                                )}
                              </div>
                            </div>
                          ))}
                        </div>
                      </div>
                    ))}
                  {Object.keys(historyData.history).length === 0 && (
                    <div className="text-center py-8 text-gray-500">
                      Geçmiş kayıt bulunmuyor
                    </div>
                  )}
                </div>
              ) : null}
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
};

export default ManagerDailyAttendance;
